<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="relative z-50" @close="handleClose">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50" aria-hidden="true" />
      </TransitionChild>

      <div class="fixed inset-0 w-screen overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="InfrastructureFicheModal__panel w-full max-w-lg transform rounded-lg bg-white text-left align-middle shadow-xl transition-all flex flex-col max-h-[90vh]"
            >
              <div
                class="InfrastructureFicheModal__header flex items-center justify-between px-6 py-4 border-b"
              >
                <h2 class="InfrastructureFicheModal__title text-lg font-semibold">
                  {{ attributeTitle ?? t('infrastructure.fiche.title') }}
                </h2>
                <button
                  type="button"
                  class="InfrastructureFicheModal__closeButton"
                  :aria-label="$t('infrastructure.fiche.closeAria', 'Fermer')"
                  @click="handleClose"
                >
                  <Icon icon="mdi:close" class="w-6 h-6" />
                </button>
              </div>
              <div class="InfrastructureFicheModal__body p-6 overflow-y-auto">
                <InfrastructureFicheContent
                  :id-infrastructure="idInfrastructure"
                  :competence-id="competenceId"
                  :feature-properties="featureProperties"
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import Icon from '@/components/ui/Icon.vue'
import InfrastructureFicheContent from '@/views/infrastructure/InfrastructureFicheContent.vue'
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from '@headlessui/vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  idInfrastructure: string | number | null
  attributeTitle?: string | null
  competenceId?: number | null
  /** Feature properties from map click; merged with API data to fill attribut values. */
  featureProperties?: Record<string, unknown> | null
}>()

const emit = defineEmits<{
  close: []
}>()

const isOpen = computed(() => props.idInfrastructure != null)

function handleClose() {
  emit('close')
}
</script>

<style lang="scss" scoped>
.InfrastructureFicheModal__panel {
  min-height: 12rem;
  border-radius: 0.75rem;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 8px 10px -6px rgba(0, 0, 0, 0.1);
}

.InfrastructureFicheModal__header {
  flex-shrink: 0;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--fiche-border-color);
  background: var(--fiche-header-bg);
  border-radius: 0.75rem 0.75rem 0 0;
}

.InfrastructureFicheModal__title {
  font-size: 1.125rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--fiche-title-color);
}

.InfrastructureFicheModal__closeButton {
  color: #9ca3af;
  padding: 0.25rem;
  border-radius: 9999px;
  cursor: pointer;
  border: none;
  background: transparent;
  /* No hover highlight */
  &:hover {
    color: #9ca3af;
    background: transparent;
  }
}

.InfrastructureFicheModal__body {
  flex: 1 1 auto;
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
}
</style>
